import React from 'react';
import axios from 'axios';
import { addDays, format, isToday } from 'date-fns';
import { FaCamera, FaCog, FaDatabase, FaPlus, FaTimes, FaTrash, FaUndoAlt, FaUtensils } from 'react-icons/fa';
import { Button, IconButton } from '../ui/Button';
import { fromDateKey, getWeekDateKeys, toDateKey } from '../../utils/dateUtils';
import styles from './NutritionPage.module.css';
import { NutritionWeekStrip } from './NutritionWeekStrip';
import { nutritionApi } from '../../services/nutritionApi';
import {
  type NutritionDayEntry,
  type NutritionEntriesByDate,
  type NutritionFoodDatabaseEntry,
  type NutritionFoodEntry,
  type NutritionMetricKey,
  type NutritionWorkspaceData,
  calculateNutritionFoodTotals,
  createEmptyNutritionDay,
  createEmptyNutritionFood,
  createNutritionFoodDatabaseEntry,
  getNutritionTargetsForDate,
  getDefaultNutritionWorkspace,
  loadNutritionWorkspace,
  normalizeNutritionWorkspace,
  saveNutritionWorkspace,
  summarizeNutritionFoods,
} from '../../services/nutritionStore';

const METRICS: Array<{ key: NutritionMetricKey; label: string; unit: 'cal' | 'g' }> = [
  { key: 'calories', label: 'Calories', unit: 'cal' },
  { key: 'carbs', label: 'Carbs', unit: 'g' },
  { key: 'protein', label: 'Protein', unit: 'g' },
  { key: 'fat', label: 'Fat', unit: 'g' },
];

const parseNumberInput = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const parseFractionInput = (value: string): number | null => {
  const [numerator, denominator, ...rest] = value.split('/');

  if (!numerator || !denominator || rest.length > 0) {
    return null;
  }

  const parsedNumerator = Number(numerator);
  const parsedDenominator = Number(denominator);

  if (!Number.isFinite(parsedNumerator) || !Number.isFinite(parsedDenominator) || parsedDenominator === 0) {
    return null;
  }

  return parsedNumerator / parsedDenominator;
};

const parseServingInput = (value: string): number => {
  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return 1;
  }

  const parts = trimmed.split(/\s+/);
  const parsed =
    parts.length === 2 && parts[1].includes('/')
      ? Number(parts[0]) + (parseFractionInput(parts[1]) ?? 0)
      : trimmed.includes('/')
        ? parseFractionInput(trimmed)
        : Number(trimmed);

  return parsed !== null && Number.isFinite(parsed) ? Math.max(0, parsed) : 1;
};

const normalizeServingUnitInput = (value: string): string => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : 'serving';
};

const formatCompactNumber = (value: number): string =>
  Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));

const formatCalories = (value: number): string => `${Math.round(value)} cal`;
const formatMetricValue = (value: number, unit: 'cal' | 'g'): string =>
  `${Math.round(value)} ${unit}`;
const formatServing = (food: Pick<NutritionFoodEntry, 'servingCount' | 'servingUnit'>): string =>
  `${formatCompactNumber(food.servingCount)} ${food.servingUnit}`;
const formatFoodSummary = (
  food: Pick<NutritionFoodEntry, 'calories' | 'carbs' | 'protein' | 'fat'>,
): string =>
  `${formatMetricValue(food.calories, 'cal')} · ${formatMetricValue(food.carbs, 'g')} C · ${formatMetricValue(food.protein, 'g')} P · ${formatMetricValue(food.fat, 'g')} F`;

const MACRO_CALORIE_FACTORS: Record<Exclude<NutritionMetricKey, 'calories'>, number> = {
  carbs: 4,
  protein: 4,
  fat: 9,
};

const MACRO_KEYS: Array<Exclude<NutritionMetricKey, 'calories'>> = ['carbs', 'protein', 'fat'];

type FoodDraft = Pick<NutritionFoodEntry, 'name' | 'servingUnit'> &
  { servingCount: string } &
  Record<NutritionMetricKey, string>;

type FoodDraftSource = Pick<
  NutritionFoodEntry,
  'name' | 'servingCount' | 'servingUnit' | 'calories' | 'carbs' | 'protein' | 'fat'
>;

type FoodScanMode = 'label' | 'meal';

type SharedFoodModalState = {
  draft: FoodDraft;
  caloriesOverridden: boolean;
  activeScanMode: FoodScanMode | null;
  scanError: string | null;
  scanFeedbackLabel: string | null;
  scanFeedbackText: string | null;
};

const hasMacroInput = (draft: Pick<FoodDraft, 'carbs' | 'protein' | 'fat'>): boolean =>
  MACRO_KEYS.some((key) => draft[key].trim().length > 0);

const calculateCaloriesFromMacroValues = (
  metrics: Record<Exclude<NutritionMetricKey, 'calories'>, number>,
): number => MACRO_KEYS.reduce((total, key) => total + metrics[key] * MACRO_CALORIE_FACTORS[key], 0);

const calculateCaloriesFromMacros = (draft: Pick<FoodDraft, 'carbs' | 'protein' | 'fat'>): string => {
  if (!hasMacroInput(draft)) {
    return '';
  }

  return formatCompactNumber(calculateCaloriesFromMacroValues({
    carbs: parseNumberInput(draft.carbs),
    protein: parseNumberInput(draft.protein),
    fat: parseNumberInput(draft.fat),
  }));
};

type FoodModalState =
  | (SharedFoodModalState & { mode: 'add'; matchedFoodId: string | null; saveToDatabase: boolean })
  | (SharedFoodModalState & { mode: 'edit'; foodId: string });

type FoodDatabaseModalState =
  | { mode: 'add'; draft: FoodDraft; caloriesOverridden: boolean }
  | { mode: 'edit'; foodId: string; draft: FoodDraft; caloriesOverridden: boolean };

const toFoodDraft = (food?: FoodDraftSource): FoodDraft => ({
  name: food?.name ?? '',
  servingCount: food ? formatCompactNumber(food.servingCount) : '1',
  servingUnit: food?.servingUnit ?? 'serving',
  calories: food ? String(food.calories) : '',
  carbs: food ? String(food.carbs) : '',
  protein: food ? String(food.protein) : '',
  fat: food ? String(food.fat) : '',
});

const draftToFoodValues = (draft: FoodDraft): Partial<Omit<NutritionFoodEntry, 'id'>> => ({
  name: draft.name.trim(),
  note: '',
  servingCount: parseServingInput(draft.servingCount),
  servingUnit: normalizeServingUnitInput(draft.servingUnit),
  calories: parseNumberInput(draft.calories),
  carbs: parseNumberInput(draft.carbs),
  protein: parseNumberInput(draft.protein),
  fat: parseNumberInput(draft.fat),
});

const draftToFoodDatabaseValues = (
  draft: FoodDraft,
): Partial<Omit<NutritionFoodDatabaseEntry, 'id' | 'updatedAt'>> => ({
  name: draft.name.trim(),
  servingCount: parseServingInput(draft.servingCount),
  servingUnit: normalizeServingUnitInput(draft.servingUnit),
  calories: parseNumberInput(draft.calories),
  carbs: parseNumberInput(draft.carbs),
  protein: parseNumberInput(draft.protein),
  fat: parseNumberInput(draft.fat),
});

const applyScannedDraftValue = (currentValue: string, scanBaseValue: string, scannedValue: string): string =>
  currentValue === scanBaseValue ? scannedValue : currentValue;

const cloneDayEntry = (entry: NutritionDayEntry, dateKey: string): NutritionDayEntry => ({
  ...entry,
  dateKey,
  targets: { ...entry.targets },
  foods: entry.foods.map((food) => ({ ...food })),
});

const areMetricTotalsEqual = (
  left: NutritionDayEntry['targets'],
  right: NutritionDayEntry['targets'],
): boolean => (
  left.calories === right.calories
  && left.carbs === right.carbs
  && left.protein === right.protein
  && left.fat === right.fat
);

export const NutritionPage: React.FC = () => {
  const [workspace, setWorkspace] = React.useState<NutritionWorkspaceData>(() => getDefaultNutritionWorkspace());
  const [isWorkspaceLoading, setIsWorkspaceLoading] = React.useState(true);
  const [workspaceError, setWorkspaceError] = React.useState<string | null>(null);
  const [selectedDateKey, setSelectedDateKey] = React.useState<string>(() => toDateKey(new Date()));
  const [showGoals, setShowGoals] = React.useState(false);
  const [showFoodDatabase, setShowFoodDatabase] = React.useState(false);
  const [foodModal, setFoodModal] = React.useState<FoodModalState | null>(null);
  const [foodDatabaseModal, setFoodDatabaseModal] = React.useState<FoodDatabaseModalState | null>(null);
  const modalRef = React.useRef<HTMLFormElement | null>(null);
  const nutritionLabelInputRef = React.useRef<HTMLInputElement | null>(null);
  const mealEstimateInputRef = React.useRef<HTMLInputElement | null>(null);
  const nutritionScanRequestIdRef = React.useRef(0);
  const workspaceRef = React.useRef(workspace);
  const latestSaveRequestIdRef = React.useRef(0);
  const pendingSaveRef = React.useRef<{ workspace: NutritionWorkspaceData; requestId: number } | null>(null);
  const isSaveInFlightRef = React.useRef(false);
  const isWorkspaceInteractionDisabled = isWorkspaceLoading;
  const todayDateKey = toDateKey(new Date());
  const isPastSelectedDate = selectedDateKey < todayDateKey;

  const entries = workspace.entries;
  const foodDatabase = workspace.foodDatabase;

  const selectedEntry = entries[selectedDateKey]
    ?? createEmptyNutritionDay(selectedDateKey, getNutritionTargetsForDate(entries, selectedDateKey));
  const selectedTotals = summarizeNutritionFoods(selectedEntry.foods);
  const foodModalTotals = foodModal
    ? {
        calories: parseNumberInput(foodModal.draft.calories) * parseServingInput(foodModal.draft.servingCount),
        carbs: parseNumberInput(foodModal.draft.carbs) * parseServingInput(foodModal.draft.servingCount),
        protein: parseNumberInput(foodModal.draft.protein) * parseServingInput(foodModal.draft.servingCount),
        fat: parseNumberInput(foodModal.draft.fat) * parseServingInput(foodModal.draft.servingCount),
      }
    : null;
  const foodDatabaseModalTotals = foodDatabaseModal
    ? {
        calories: parseNumberInput(foodDatabaseModal.draft.calories) * parseServingInput(foodDatabaseModal.draft.servingCount),
        carbs: parseNumberInput(foodDatabaseModal.draft.carbs) * parseServingInput(foodDatabaseModal.draft.servingCount),
        protein: parseNumberInput(foodDatabaseModal.draft.protein) * parseServingInput(foodDatabaseModal.draft.servingCount),
        fat: parseNumberInput(foodDatabaseModal.draft.fat) * parseServingInput(foodDatabaseModal.draft.servingCount),
      }
    : null;
  const weekDateKeys = React.useMemo(() => getWeekDateKeys(selectedDateKey), [selectedDateKey]);
  const foodModalName = foodModal?.draft.name.trim().toLocaleLowerCase() ?? '';
  const exactFoodDatabaseMatch = foodModalName
    ? foodDatabase.find((food) => food.name.toLocaleLowerCase() === foodModalName) ?? null
    : null;
  const foodDatabaseSuggestions = foodModalName
    ? foodDatabase
        .filter((food) => food.name.toLocaleLowerCase().includes(foodModalName))
        .filter((food) => food.id !== exactFoodDatabaseMatch?.id)
        .slice(0, 5)
    : foodDatabase.slice(0, 5);
  const isFoodModalScanning = foodModal?.activeScanMode !== null;

  React.useEffect(() => {
    workspaceRef.current = workspace;
  }, [workspace]);

  const syncWorkspaceVersion = React.useCallback((version: number) => {
    workspaceRef.current = normalizeNutritionWorkspace({
      ...workspaceRef.current,
      version,
    });
    setWorkspace(workspaceRef.current);

    if (!pendingSaveRef.current) {
      return;
    }

    pendingSaveRef.current = {
      ...pendingSaveRef.current,
      workspace: normalizeNutritionWorkspace({
        ...pendingSaveRef.current.workspace,
        version,
      }),
    };
  }, []);

  const flushSaveQueue = React.useCallback(() => {
    if (isSaveInFlightRef.current || !pendingSaveRef.current) {
      return;
    }

    const { workspace: workspaceToSave, requestId } = pendingSaveRef.current;

    pendingSaveRef.current = null;
    isSaveInFlightRef.current = true;

    saveNutritionWorkspace(workspaceToSave)
      .then((savedWorkspace) => {
        if (requestId !== latestSaveRequestIdRef.current) {
          syncWorkspaceVersion(savedWorkspace.version);
          return;
        }

        workspaceRef.current = savedWorkspace;
        setWorkspace(savedWorkspace);
      })
      .catch((error) => {
        if (requestId !== latestSaveRequestIdRef.current) {
          return;
        }

        setWorkspaceError(
          axios.isAxiosError(error) && error.response?.status === 409
            ? 'Nutrition was updated in another tab or device. Refresh to sync before saving again.'
            : 'Unable to save nutrition right now. Your last change is still visible locally.',
        );
      })
      .finally(() => {
        isSaveInFlightRef.current = false;
        flushSaveQueue();
      });
  }, [syncWorkspaceVersion]);

  const persistWorkspace = React.useCallback((updater: (previous: NutritionWorkspaceData) => NutritionWorkspaceData) => {
    if (isWorkspaceLoading) {
      return;
    }

    const nextWorkspace = normalizeNutritionWorkspace(updater(workspaceRef.current));
    const requestId = latestSaveRequestIdRef.current + 1;

    latestSaveRequestIdRef.current = requestId;
    pendingSaveRef.current = { workspace: nextWorkspace, requestId };
    workspaceRef.current = nextWorkspace;
    setWorkspace(nextWorkspace);
    setWorkspaceError(null);
    flushSaveQueue();
  }, [flushSaveQueue, isWorkspaceLoading]);

  const persistEntries = React.useCallback((updater: (previous: NutritionEntriesByDate) => NutritionEntriesByDate) => {
    persistWorkspace((previous) => ({
      ...previous,
      entries: updater(previous.entries),
    }));
  }, [persistWorkspace]);

  const persistFoodDatabase = React.useCallback((updater: (previous: NutritionFoodDatabaseEntry[]) => NutritionFoodDatabaseEntry[]) => {
    persistWorkspace((previous) => ({
      ...previous,
      foodDatabase: updater(previous.foodDatabase).sort((left, right) => left.name.localeCompare(right.name)),
    }));
  }, [persistWorkspace]);

  const updateSelectedEntry = (updater: (entry: NutritionDayEntry) => NutritionDayEntry) => {
    persistEntries((previous) => {
      const current = cloneDayEntry(
        previous[selectedDateKey]
          ?? createEmptyNutritionDay(selectedDateKey, getNutritionTargetsForDate(previous, selectedDateKey)),
        selectedDateKey,
      );
      const nextEntry = {
        ...updater(current),
        dateKey: selectedDateKey,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...previous,
        [selectedDateKey]: nextEntry,
      };
    });
  };

  const updateSelectedTarget = (metricKey: NutritionMetricKey, value: string) => {
    if (isPastSelectedDate) {
      return;
    }

    persistEntries((previous) => {
      const currentTargets = getNutritionTargetsForDate(previous, selectedDateKey);
      const parsedValue = parseNumberInput(value);
      const currentEntry = cloneDayEntry(
        previous[selectedDateKey] ?? createEmptyNutritionDay(selectedDateKey, currentTargets),
        selectedDateKey,
      );

      const nextTargets =
        metricKey === 'calories'
          ? {
              ...currentEntry.targets,
              calories: parsedValue,
            }
          : (() => {
              const nextMacroTargets = {
                ...currentEntry.targets,
                [metricKey]: parsedValue,
              };

              return {
                ...nextMacroTargets,
                calories: calculateCaloriesFromMacroValues({
                  carbs: nextMacroTargets.carbs,
                  protein: nextMacroTargets.protein,
                  fat: nextMacroTargets.fat,
                }),
              };
            })();

      const timestamp = new Date().toISOString();
      const nextEntries: NutritionEntriesByDate = {
        ...previous,
        [selectedDateKey]: {
          ...currentEntry,
          targets: nextTargets,
          updatedAt: timestamp,
        },
      };

      for (const dateKey of Object.keys(previous)
        .filter((candidateDateKey) => candidateDateKey > selectedDateKey)
        .sort((left, right) => left.localeCompare(right))) {
        const entry = previous[dateKey];

        if (!entry) {
          continue;
        }

        if (!areMetricTotalsEqual(entry.targets, currentTargets)) {
          break;
        }

        nextEntries[dateKey] = {
          ...cloneDayEntry(entry, dateKey),
          targets: { ...nextTargets },
          updatedAt: timestamp,
        };
      }

      return nextEntries;
    });
  };

  const resetSelectedDay = () => {
    persistEntries((previous) => {
      const existingEntry = previous[selectedDateKey];

      if (!existingEntry) {
        return previous;
      }

      if (isPastSelectedDate) {
        if (existingEntry.foods.length === 0) {
          return previous;
        }

        return {
          ...previous,
          [selectedDateKey]: {
            ...cloneDayEntry(existingEntry, selectedDateKey),
            foods: [],
            updatedAt: new Date().toISOString(),
          },
        };
      }

      const { [selectedDateKey]: _removed, ...rest } = previous;
      return rest;
    });
  };

  const shiftWeek = (deltaWeeks: number) => {
    const nextDate = addDays(fromDateKey(selectedDateKey), deltaWeeks * 7);
    setSelectedDateKey(toDateKey(nextDate));
  };

  const openAddFoodModal = () => {
    if (isWorkspaceInteractionDisabled) {
      return;
    }

    setFoodModal({
      mode: 'add',
      draft: toFoodDraft(),
      caloriesOverridden: false,
      matchedFoodId: null,
      saveToDatabase: true,
      activeScanMode: null,
      scanError: null,
      scanFeedbackLabel: null,
      scanFeedbackText: null,
    });
  };

  const openEditFoodModal = (food: NutritionFoodEntry) => {
    if (isWorkspaceInteractionDisabled) {
      return;
    }

    setFoodModal({
      mode: 'edit',
      foodId: food.id,
      draft: toFoodDraft(food),
      caloriesOverridden: true,
      activeScanMode: null,
      scanError: null,
      scanFeedbackLabel: null,
      scanFeedbackText: null,
    });
  };

  const openAddFoodDatabaseModal = () => {
    if (isWorkspaceInteractionDisabled) {
      return;
    }

    setFoodDatabaseModal({ mode: 'add', draft: toFoodDraft(), caloriesOverridden: false });
  };

  const openEditFoodDatabaseModal = (food: NutritionFoodDatabaseEntry) => {
    if (isWorkspaceInteractionDisabled) {
      return;
    }

    setFoodDatabaseModal({ mode: 'edit', foodId: food.id, draft: toFoodDraft(food), caloriesOverridden: true });
  };

  const applyFoodDatabaseEntryToModal = (food: NutritionFoodDatabaseEntry) => {
    setFoodModal((current) => {
      if (!current || current.mode !== 'add') {
        return current;
      }

      return {
        ...current,
        draft: toFoodDraft(food),
        caloriesOverridden: true,
        matchedFoodId: food.id,
        saveToDatabase: false,
        scanError: null,
        scanFeedbackLabel: null,
        scanFeedbackText: null,
      };
    });
  };

  const updateFoodDraft = (updates: Partial<FoodDraft>) => {
    setFoodModal((current) => {
      if (!current) {
        return current;
      }

      const nextDraft = { ...current.draft, ...updates };
      const caloriesUpdated = Object.prototype.hasOwnProperty.call(updates, 'calories');
      const macrosUpdated = MACRO_KEYS.some((key) => Object.prototype.hasOwnProperty.call(updates, key));
      const nameUpdated = Object.prototype.hasOwnProperty.call(updates, 'name');

      if (current.mode === 'add' && nameUpdated) {
        const matchedFood = foodDatabase.find(
          (food) => food.name.toLocaleLowerCase() === nextDraft.name.trim().toLocaleLowerCase(),
        );

        if (matchedFood) {
          return {
            ...current,
            draft: toFoodDraft(matchedFood),
            caloriesOverridden: true,
            matchedFoodId: matchedFood.id,
            saveToDatabase: false,
          };
        }
      }

      if (current.mode === 'add' && macrosUpdated) {
        nextDraft.calories = calculateCaloriesFromMacros(nextDraft);
      }

      return {
        ...current,
        draft: nextDraft,
        caloriesOverridden: current.mode === 'add' && macrosUpdated ? false : current.caloriesOverridden || caloriesUpdated,
        scanError: null,
        ...(current.mode === 'add' && nameUpdated ? { matchedFoodId: null } : {}),
      };
    });
  };

  const updateFoodModalSaveToDatabase = (saveToDatabase: boolean) => {
    setFoodModal((current) => (current?.mode === 'add' ? { ...current, saveToDatabase } : current));
  };

  const applyFoodModalScanResult = (
    scanRequestId: number,
    scanBaseDraft: FoodDraft,
    result: {
      name: string;
      servingCount?: number;
      servingUnit?: string;
      calories: number;
      carbs: number;
      protein: number;
      fat: number;
      feedbackLabel: string;
      feedbackText: string;
    },
  ) => {
    setFoodModal((current) => {
      if (!current || current.activeScanMode === null || nutritionScanRequestIdRef.current !== scanRequestId) {
        return current;
      }

      const nextDraft = {
        ...current.draft,
        name: applyScannedDraftValue(current.draft.name, scanBaseDraft.name, result.name.trim() || current.draft.name),
        servingCount: applyScannedDraftValue(
          current.draft.servingCount,
          scanBaseDraft.servingCount,
          result.servingCount !== undefined ? formatCompactNumber(result.servingCount) : current.draft.servingCount,
        ),
        servingUnit: applyScannedDraftValue(
          current.draft.servingUnit,
          scanBaseDraft.servingUnit,
          result.servingUnit ?? current.draft.servingUnit,
        ),
        calories: applyScannedDraftValue(
          current.draft.calories,
          scanBaseDraft.calories,
          formatCompactNumber(result.calories),
        ),
        carbs: applyScannedDraftValue(current.draft.carbs, scanBaseDraft.carbs, formatCompactNumber(result.carbs)),
        protein: applyScannedDraftValue(
          current.draft.protein,
          scanBaseDraft.protein,
          formatCompactNumber(result.protein),
        ),
        fat: applyScannedDraftValue(current.draft.fat, scanBaseDraft.fat, formatCompactNumber(result.fat)),
      };
      const macrosChangedDuringScan = MACRO_KEYS.some((key) => current.draft[key] !== scanBaseDraft[key]);
      const shouldRecomputeCalories = !current.caloriesOverridden && macrosChangedDuringScan;

      if (shouldRecomputeCalories) {
        nextDraft.calories = calculateCaloriesFromMacros(nextDraft);
      }

      const scanApplied = Object.keys(nextDraft).some((key) => nextDraft[key as keyof FoodDraft] !== current.draft[key as keyof FoodDraft]);
      const matchedFood =
        current.mode === 'add'
          ? foodDatabase.find((food) => food.name.toLocaleLowerCase() === nextDraft.name.trim().toLocaleLowerCase())
          : null;

      return {
        ...current,
        draft: nextDraft,
        caloriesOverridden: shouldRecomputeCalories ? false : scanApplied ? true : current.caloriesOverridden,
        activeScanMode: null,
        scanError: null,
        scanFeedbackLabel: result.feedbackLabel,
        scanFeedbackText: result.feedbackText,
        ...(current.mode === 'add'
          ? {
              matchedFoodId: matchedFood?.id ?? (scanApplied ? null : current.matchedFoodId),
              saveToDatabase: matchedFood ? false : current.saveToDatabase,
            }
          : {}),
      };
    });
  };

  const handleNutritionLabelFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file || !foodModal) {
      return;
    }

    const scanRequestId = nutritionScanRequestIdRef.current + 1;
    const scanBaseDraft = foodModal.draft;
    nutritionScanRequestIdRef.current = scanRequestId;

    setFoodModal((current) =>
      current
        ? {
            ...current,
            activeScanMode: 'label',
            scanError: null,
            scanFeedbackLabel: null,
            scanFeedbackText: null,
          }
        : current,
    );

    try {
      const result = await nutritionApi.scanLabel(file);
      applyFoodModalScanResult(scanRequestId, scanBaseDraft, {
        name: result.name,
        servingCount: 1,
        servingUnit: 'serving',
        calories: result.calories,
        carbs: result.carbs,
        protein: result.protein,
        fat: result.fat,
        feedbackLabel: 'Detected serving size',
        feedbackText: result.servingSizeText ?? '1 serving',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to read that nutrition label.';

      setFoodModal((current) =>
        current && current.activeScanMode === 'label' && nutritionScanRequestIdRef.current === scanRequestId
          ? {
              ...current,
              activeScanMode: null,
              scanError: message,
              scanFeedbackLabel: null,
              scanFeedbackText: null,
            }
          : current,
      );
    } finally {
      event.target.value = '';
    }
  };

  const handleMealEstimateFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file || !foodModal) {
      return;
    }

    const scanRequestId = nutritionScanRequestIdRef.current + 1;
    const scanBaseDraft = foodModal.draft;
    nutritionScanRequestIdRef.current = scanRequestId;

    setFoodModal((current) =>
      current
        ? {
            ...current,
            activeScanMode: 'meal',
            scanError: null,
            scanFeedbackLabel: null,
            scanFeedbackText: null,
          }
        : current,
    );

    try {
      const result = await nutritionApi.estimateMeal(file);
      applyFoodModalScanResult(scanRequestId, scanBaseDraft, {
        name: result.name,
        servingCount: result.servingCount,
        servingUnit: result.servingUnit,
        calories: result.calories,
        carbs: result.carbs,
        protein: result.protein,
        fat: result.fat,
        feedbackLabel: 'Estimated meal',
        feedbackText: result.servingSizeText
          ? `${result.servingSizeText}. Double-check portions, oils, and sauces.`
          : 'Approximate macros from the meal photo. Double-check portions, oils, and sauces.',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to estimate that meal photo.';

      setFoodModal((current) =>
        current && current.activeScanMode === 'meal' && nutritionScanRequestIdRef.current === scanRequestId
          ? {
              ...current,
              activeScanMode: null,
              scanError: message,
              scanFeedbackLabel: null,
              scanFeedbackText: null,
            }
          : current,
      );
    } finally {
      event.target.value = '';
    }
  };

  const updateFoodDatabaseDraft = (updates: Partial<FoodDraft>) => {
    setFoodDatabaseModal((current) => {
      if (!current) {
        return current;
      }

      const nextDraft = { ...current.draft, ...updates };
      const caloriesUpdated = Object.prototype.hasOwnProperty.call(updates, 'calories');
      const macrosUpdated = MACRO_KEYS.some((key) => Object.prototype.hasOwnProperty.call(updates, key));

      if (current.mode === 'add' && macrosUpdated) {
        nextDraft.calories = calculateCaloriesFromMacros(nextDraft);
      }

      return {
        ...current,
        draft: nextDraft,
        caloriesOverridden: current.mode === 'add' && macrosUpdated ? false : current.caloriesOverridden || caloriesUpdated,
      };
    });
  };

  const saveFoodModal = () => {
    if (!foodModal) {
      return;
    }

    const values = draftToFoodValues(foodModal.draft);

    if (foodModal.mode === 'add') {
      updateSelectedEntry((entry) => ({
        ...entry,
        foods: [...entry.foods, createEmptyNutritionFood(values)],
      }));

      if (foodModal.saveToDatabase && !foodModal.matchedFoodId && values.name) {
        const databaseValues = draftToFoodDatabaseValues(foodModal.draft);
        persistFoodDatabase((previous) => [
          ...previous.filter((food) => food.name.toLocaleLowerCase() !== databaseValues.name?.toLocaleLowerCase()),
          createNutritionFoodDatabaseEntry(databaseValues),
        ]);
      }
    } else {
      updateFood(foodModal.foodId, values);
    }

    setFoodModal(null);
  };

  const saveFoodDatabaseModal = () => {
    if (!foodDatabaseModal) {
      return;
    }

    const values = draftToFoodDatabaseValues(foodDatabaseModal.draft);

    if (!values.name) {
      return;
    }

    if (foodDatabaseModal.mode === 'add') {
      persistFoodDatabase((previous) => [
        ...previous.filter((food) => food.name.toLocaleLowerCase() !== values.name?.toLocaleLowerCase()),
        createNutritionFoodDatabaseEntry(values),
      ]);
    } else {
      persistFoodDatabase((previous) =>
        previous.map((food) =>
          food.id === foodDatabaseModal.foodId
            ? {
                ...food,
                ...values,
                updatedAt: new Date().toISOString(),
              }
            : food,
        ),
      );
    }

    setFoodDatabaseModal(null);
  };

  const updateFood = (foodId: string, updates: Partial<NutritionFoodEntry>) => {
    updateSelectedEntry((entry) => ({
      ...entry,
      foods: entry.foods.map((food) => (food.id === foodId ? { ...food, ...updates } : food)),
    }));
  };

  const removeFood = (foodId: string) => {
    updateSelectedEntry((entry) => ({
      ...entry,
      foods: entry.foods.filter((food) => food.id !== foodId),
    }));
  };

  const removeFoodFromModal = () => {
    if (!foodModal || foodModal.mode !== 'edit') {
      return;
    }

    removeFood(foodModal.foodId);
    setFoodModal(null);
  };

  const removeFoodDatabaseEntryFromModal = () => {
    if (!foodDatabaseModal || foodDatabaseModal.mode !== 'edit') {
      return;
    }

    persistFoodDatabase((previous) => previous.filter((food) => food.id !== foodDatabaseModal.foodId));
    setFoodDatabaseModal(null);
  };

  const selectedDate = fromDateKey(selectedDateKey);

  React.useEffect(() => {
    let isMounted = true;

    setIsWorkspaceLoading(true);
    loadNutritionWorkspace()
      .then((loadedWorkspace) => {
        if (!isMounted) {
          return;
        }

        if (
          latestSaveRequestIdRef.current > 0
          || pendingSaveRef.current
          || isSaveInFlightRef.current
        ) {
          return;
        }

        workspaceRef.current = loadedWorkspace;
        setWorkspace(loadedWorkspace);
        setWorkspaceError(null);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setWorkspaceError('Unable to load saved nutrition. Showing an empty log until the server responds.');
      })
      .finally(() => {
        if (isMounted) {
          setIsWorkspaceLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (!foodModal && !foodDatabaseModal) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setFoodModal(null);
        setFoodDatabaseModal(null);
        return;
      }

      if (event.key !== 'Tab' || !modalRef.current) {
        return;
      }

      const focusable = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])',
        ),
      );

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];

      if (!firstElement || !lastElement) {
        return;
      }

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [foodModal, foodDatabaseModal]);

  return (
    <div className={styles.page}>
      <header className={`${styles.header} mobile-page-header-anchor`}>
        <div>
          <h1 className={styles.title}>Nutrition</h1>
          <p className={styles.headerMeta}>
            {format(selectedDate, 'EEEE, MMMM d')}
            {isToday(selectedDate) ? ' · Today' : ''}
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="secondary"
            size="md"
            onClick={() => setSelectedDateKey(toDateKey(new Date()))}
          >
            Jump to today
          </Button>
          <Button
            variant="ghost"
            size="md"
            leftIcon={FaUndoAlt({ 'aria-hidden': 'true' })}
            onClick={resetSelectedDay}
            disabled={isWorkspaceInteractionDisabled}
          >
            Reset day
          </Button>
          <Button
            variant="primary"
            size="md"
            leftIcon={FaPlus({ 'aria-hidden': 'true' })}
            onClick={openAddFoodModal}
            disabled={isWorkspaceInteractionDisabled}
          >
            Add food
          </Button>
        </div>
      </header>

      <section className={styles.panel}>
        <div className={styles.weekHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Date</h2>
            <p className={styles.sectionHint}>
              {workspaceError
                ? workspaceError
                : isWorkspaceLoading
                  ? 'Loading saved nutrition…'
                  : isToday(selectedDate)
                    ? 'Today'
                    : `Saved at ${format(new Date(selectedEntry.updatedAt), 'p')}`}
            </p>
          </div>
        </div>

        <NutritionWeekStrip
          weekDateKeys={weekDateKeys}
          selectedDateKey={selectedDateKey}
          onSelectDate={setSelectedDateKey}
          onShiftWeek={shiftWeek}
          getDayMeta={(dateKey) => {
            const dayEntry = entries[dateKey];
            const dayTotals = summarizeNutritionFoods(dayEntry?.foods ?? []);
            return dayEntry && dayEntry.foods.length > 0 ? formatCalories(dayTotals.calories) : 'No log';
          }}
        />
      </section>

      <section className={styles.panel}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Daily totals</h2>
            <p className={styles.sectionHint}>Percentages compare intake against this day&apos;s saved goals.</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={FaCog({ 'aria-hidden': 'true' })}
            onClick={() => setShowGoals((previous) => !previous)}
            aria-expanded={showGoals}
            aria-controls="nutrition-goals-panel"
            disabled={isWorkspaceInteractionDisabled}
          >
            Goals
          </Button>
        </div>

        {showGoals ? (
          <div id="nutrition-goals-panel" className={styles.goalDrawer}>
            <div className={styles.goalDrawerHeader}>
              <h3 className={styles.goalDrawerTitle}>Goals for {format(selectedDate, 'MMM d')}</h3>
              <p className={styles.goalDrawerHint}>
                {isPastSelectedDate
                  ? 'Past goals are read-only.'
                  : 'Goals carry forward until you change them.'}
              </p>
            </div>
            <div className={styles.targetsGrid}>
              {METRICS.map((metric) => (
                <label key={metric.key} className={styles.field}>
                  <span className={styles.fieldLabel}>{metric.label}</span>
                  <input
                    className={styles.input}
                    type="number"
                    min="0"
                    step="1"
                    value={selectedEntry.targets[metric.key]}
                    disabled={isWorkspaceInteractionDisabled || isPastSelectedDate}
                    onChange={(event) => updateSelectedTarget(metric.key, event.target.value)}
                  />
                </label>
              ))}
            </div>
          </div>
        ) : null}

        <div className={styles.metricStack}>
          {METRICS.map((metric) => {
            const consumed = selectedTotals[metric.key];
            const target = selectedEntry.targets[metric.key];
            const remaining = target - consumed;
            const progress = target > 0 ? Math.min((consumed / target) * 100, 100) : 0;

            return (
              <article key={metric.key} className={styles.metricRow}>
                <div className={styles.metricRowHeader}>
                  <div>
                    <h3 className={styles.metricTitle}>{metric.label}</h3>
                    <p className={styles.metricNumbers}>
                      {formatMetricValue(consumed, metric.unit)} of {formatMetricValue(target, metric.unit)}
                    </p>
                  </div>
                  <strong className={styles.metricPercent}>{Math.round(progress)}%</strong>
                </div>
                <div className={styles.progressTrack} aria-hidden="true">
                  <span className={styles.progressFill} style={{ width: `${progress}%` }} />
                </div>
                <p className={remaining >= 0 ? styles.metricRemaining : styles.metricOver}>
                  {formatMetricValue(Math.abs(remaining), metric.unit)} {remaining >= 0 ? 'left' : 'over'}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      <section className={styles.panel}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Foods</h2>
            <p className={styles.sectionHint}>Everything eaten on {format(selectedDate, 'MMM d')}.</p>
          </div>
          <Button
            variant="primary"
            size="md"
            leftIcon={FaPlus({ 'aria-hidden': 'true' })}
            onClick={openAddFoodModal}
            disabled={isWorkspaceInteractionDisabled}
          >
            Add food
          </Button>
        </div>

        {selectedEntry.foods.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyTitle}>No foods logged yet.</p>
            <p className={styles.emptyHint}>Add foods for the day to start filling the totals.</p>
          </div>
        ) : (
          <div className={styles.foodList}>
            <div className={styles.foodListHeader} aria-hidden="true">
              <span>Food</span>
              <span>Serving</span>
              <span>Total</span>
            </div>
            {selectedEntry.foods.map((food, index) => {
              const foodTotals = calculateNutritionFoodTotals(food);

              return (
                <button
                  key={food.id}
                  type="button"
                  className={styles.foodRow}
                  onClick={() => openEditFoodModal(food)}
                  aria-label={`Edit food ${index + 1}: ${food.name || 'Untitled food'}`}
                  disabled={isWorkspaceInteractionDisabled}
                >
                  <span className={styles.foodName}>{food.name || 'Untitled food'}</span>
                  <span className={styles.foodServing}>{formatServing(food)}</span>
                  <span className={styles.foodTotals}>
                    <span className={styles.foodCalories}>{formatCalories(foodTotals.calories)}</span>
                    <span className={styles.foodMacroTotals}>
                      {formatMetricValue(foodTotals.carbs, 'g')} C · {formatMetricValue(foodTotals.protein, 'g')} P ·{' '}
                      {formatMetricValue(foodTotals.fat, 'g')} F
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className={styles.panel}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Food database</h2>
            <p className={styles.sectionHint}>Saved foods appear as description suggestions when adding food.</p>
          </div>
          <div className={styles.headerActions}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={FaDatabase({ 'aria-hidden': 'true' })}
              onClick={() => setShowFoodDatabase((previous) => !previous)}
              aria-expanded={showFoodDatabase}
              aria-controls="nutrition-food-database-panel"
              disabled={isWorkspaceInteractionDisabled}
            >
              {showFoodDatabase ? 'Hide database' : 'Manage database'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={FaPlus({ 'aria-hidden': 'true' })}
              onClick={openAddFoodDatabaseModal}
              disabled={isWorkspaceInteractionDisabled}
            >
              New saved food
            </Button>
          </div>
        </div>

        {showFoodDatabase ? (
          <div id="nutrition-food-database-panel" className={styles.databasePanel}>
            {foodDatabase.length === 0 ? (
              <div className={styles.emptyState}>
                <p className={styles.emptyTitle}>No saved foods yet.</p>
                <p className={styles.emptyHint}>Save a food while logging it or add one here.</p>
              </div>
            ) : (
              <div className={styles.databaseList}>
                {foodDatabase.map((food, index) => (
                  <button
                    key={food.id}
                    type="button"
                    className={styles.databaseRow}
                    onClick={() => openEditFoodDatabaseModal(food)}
                    aria-label={`Edit saved food ${index + 1}: ${food.name}`}
                    disabled={isWorkspaceInteractionDisabled}
                  >
                    <span className={styles.foodName}>{food.name}</span>
                    <span className={styles.foodServing}>{formatServing(food)}</span>
                    <span className={styles.foodMacroTotals}>{formatFoodSummary(food)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </section>

      {foodModal ? (
        <div className={styles.modalOverlay} onClick={(event) => event.target === event.currentTarget && setFoodModal(null)}>
          <form
            ref={modalRef}
            className={styles.foodModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="food-modal-title"
            onSubmit={(event) => {
              event.preventDefault();
              saveFoodModal();
            }}
          >
            <div className={styles.modalHeader}>
              <div>
                <h2 id="food-modal-title" className={styles.modalTitle}>
                  {foodModal.mode === 'add' ? 'Add food' : 'Edit food'}
                </h2>
                <p className={styles.modalHint}>{format(selectedDate, 'EEEE, MMM d')}</p>
              </div>
              <IconButton
                icon={FaTimes({ 'aria-hidden': 'true' })}
                variant="ghost"
                size="sm"
                onClick={() => setFoodModal(null)}
                aria-label="Close food entry"
              />
            </div>

            <div className={styles.modalBody}>
              <div className={styles.scanRow}>
                <div className={styles.scanHeader}>
                  <span className={styles.fieldLabel}>Photo</span>
                  <p className={styles.fieldHint}>Use a label photo or estimate from the meal itself.</p>
                </div>
                <div className={styles.scanActions}>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={FaCamera({ 'aria-hidden': 'true' })}
                    onClick={() => nutritionLabelInputRef.current?.click()}
                    disabled={isFoodModalScanning}
                  >
                    {foodModal.activeScanMode === 'label' ? 'Reading label...' : 'Scan label'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={FaUtensils({ 'aria-hidden': 'true' })}
                    onClick={() => mealEstimateInputRef.current?.click()}
                    disabled={isFoodModalScanning}
                  >
                    {foodModal.activeScanMode === 'meal' ? 'Estimating meal...' : 'Estimate meal'}
                  </Button>
                </div>
                <input
                  ref={nutritionLabelInputRef}
                  className={styles.hiddenFileInput}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  tabIndex={-1}
                  aria-label="Upload nutrition label photo"
                  onChange={handleNutritionLabelFileChange}
                />
                <input
                  ref={mealEstimateInputRef}
                  className={styles.hiddenFileInput}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  tabIndex={-1}
                  aria-label="Upload meal photo"
                  onChange={handleMealEstimateFileChange}
                />
              </div>

              {foodModal.scanError ? (
                <p className={styles.scanError} role="alert">
                  {foodModal.scanError}
                </p>
              ) : null}

              {foodModal.scanFeedbackText && foodModal.scanFeedbackLabel ? (
                <p className={styles.fieldHint} aria-live="polite">
                  {foodModal.scanFeedbackLabel}: {foodModal.scanFeedbackText}
                </p>
              ) : null}

              <label className={styles.field}>
                <span className={styles.fieldLabel}>Description</span>
                <input
                  className={styles.input}
                  type="text"
                  value={foodModal.draft.name}
                  aria-label="Food description"
                  onChange={(event) => updateFoodDraft({ name: event.target.value })}
                  list={foodModal.mode === 'add' ? 'nutrition-food-description-options' : undefined}
                  placeholder="Chicken burrito bowl, protein shake, banana..."
                  autoFocus
                />
                {foodModal.mode === 'add' ? (
                  <>
                    <datalist id="nutrition-food-description-options">
                      {foodDatabase.map((food) => (
                        <option key={food.id} value={food.name} />
                      ))}
                    </datalist>
                    {exactFoodDatabaseMatch ? (
                      <p className={styles.fieldHint}>Using saved food: {exactFoodDatabaseMatch.name}</p>
                    ) : foodDatabaseSuggestions.length > 0 ? (
                      <div className={styles.suggestionList} aria-label="Saved food suggestions">
                        {foodDatabaseSuggestions.map((food) => (
                          <button
                            key={food.id}
                            type="button"
                            className={styles.suggestionButton}
                            onClick={() => applyFoodDatabaseEntryToModal(food)}
                          >
                            <span>{food.name}</span>
                            <span>
                              {formatServing(food)} · {formatFoodSummary(food)}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className={styles.fieldHint}>No saved food matches yet.</p>
                    )}
                  </>
                ) : null}
              </label>

              <div className={styles.servingGrid}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}># of servings</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={foodModal.draft.servingCount}
                    aria-label="# of servings"
                    onChange={(event) => updateFoodDraft({ servingCount: event.target.value })}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Serving unit</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={foodModal.draft.servingUnit}
                    aria-label="Serving unit"
                    onChange={(event) => updateFoodDraft({ servingUnit: event.target.value })}
                    placeholder="serving, cup, ounce..."
                  />
                </label>
              </div>

              <div className={styles.modalMetricsGrid}>
                {METRICS.map((metric) => (
                  <label key={metric.key} className={styles.field}>
                    <span className={styles.fieldLabel}>{metric.label} per serving</span>
                    <input
                      className={styles.input}
                      type="number"
                      min="0"
                      step="0.1"
                      value={foodModal.draft[metric.key]}
                      aria-label={`${metric.label} per serving`}
                      onChange={(event) => updateFoodDraft({ [metric.key]: event.target.value })}
                    />
                  </label>
                ))}
              </div>

              {foodModalTotals ? (
                <div className={styles.modalTotals} aria-live="polite">
                  <span className={styles.modalTotalsLabel}>Total</span>
                  <span>
                    {formatCalories(foodModalTotals.calories)} · {formatMetricValue(foodModalTotals.carbs, 'g')} carbs ·{' '}
                    {formatMetricValue(foodModalTotals.protein, 'g')} protein · {formatMetricValue(foodModalTotals.fat, 'g')} fat
                  </span>
                </div>
              ) : null}

              {foodModal.mode === 'add' ? (
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={foodModal.saveToDatabase}
                    disabled={Boolean(foodModal.matchedFoodId)}
                    onChange={(event) => updateFoodModalSaveToDatabase(event.target.checked)}
                  />
                  <span>{foodModal.matchedFoodId ? 'Already saved in the food database' : 'Save this food to the database'}</span>
                </label>
              ) : null}
            </div>

            <div className={styles.modalActions}>
              {foodModal.mode === 'edit' ? (
                <Button
                  variant="danger"
                  size="md"
                  leftIcon={FaTrash({ 'aria-hidden': 'true' })}
                  onClick={removeFoodFromModal}
                >
                  Delete food
                </Button>
              ) : <span />}
              <div className={styles.modalPrimaryActions}>
                <Button variant="secondary" size="md" onClick={() => setFoodModal(null)}>
                  Cancel
                </Button>
                <Button variant="primary" size="md" type="submit" disabled={isFoodModalScanning}>
                  {foodModal.mode === 'add' ? 'Add food' : 'Save food'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      ) : null}

      {foodDatabaseModal ? (
        <div className={styles.modalOverlay} onClick={(event) => event.target === event.currentTarget && setFoodDatabaseModal(null)}>
          <form
            ref={modalRef}
            className={styles.foodModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="food-database-modal-title"
            onSubmit={(event) => {
              event.preventDefault();
              saveFoodDatabaseModal();
            }}
          >
            <div className={styles.modalHeader}>
              <div>
                <h2 id="food-database-modal-title" className={styles.modalTitle}>
                  {foodDatabaseModal.mode === 'add' ? 'Add saved food' : 'Edit saved food'}
                </h2>
                <p className={styles.modalHint}>Reusable food details for future logs.</p>
              </div>
              <IconButton
                icon={FaTimes({ 'aria-hidden': 'true' })}
                variant="ghost"
                size="sm"
                onClick={() => setFoodDatabaseModal(null)}
                aria-label="Close saved food entry"
              />
            </div>

            <div className={styles.modalBody}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Description</span>
                <input
                  className={styles.input}
                  type="text"
                  value={foodDatabaseModal.draft.name}
                  aria-label="Saved food description"
                  onChange={(event) => updateFoodDatabaseDraft({ name: event.target.value })}
                  placeholder="Chicken burrito bowl, protein shake, banana..."
                  autoFocus
                />
              </label>

              <div className={styles.servingGrid}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}># of servings</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={foodDatabaseModal.draft.servingCount}
                    aria-label="Saved food # of servings"
                    onChange={(event) => updateFoodDatabaseDraft({ servingCount: event.target.value })}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Serving unit</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={foodDatabaseModal.draft.servingUnit}
                    aria-label="Saved food serving unit"
                    onChange={(event) => updateFoodDatabaseDraft({ servingUnit: event.target.value })}
                    placeholder="serving, cup, ounce..."
                  />
                </label>
              </div>

              <div className={styles.modalMetricsGrid}>
                {METRICS.map((metric) => (
                  <label key={metric.key} className={styles.field}>
                    <span className={styles.fieldLabel}>{metric.label} per serving</span>
                    <input
                      className={styles.input}
                      type="number"
                      min="0"
                      step="0.1"
                      value={foodDatabaseModal.draft[metric.key]}
                      aria-label={`Saved food ${metric.label} per serving`}
                      onChange={(event) => updateFoodDatabaseDraft({ [metric.key]: event.target.value })}
                    />
                  </label>
                ))}
              </div>

              {foodDatabaseModalTotals ? (
                <div className={styles.modalTotals} aria-live="polite">
                  <span className={styles.modalTotalsLabel}>Total</span>
                  <span>
                    {formatCalories(foodDatabaseModalTotals.calories)} · {formatMetricValue(foodDatabaseModalTotals.carbs, 'g')} carbs ·{' '}
                    {formatMetricValue(foodDatabaseModalTotals.protein, 'g')} protein · {formatMetricValue(foodDatabaseModalTotals.fat, 'g')} fat
                  </span>
                </div>
              ) : null}
            </div>

            <div className={styles.modalActions}>
              {foodDatabaseModal.mode === 'edit' ? (
                <Button
                  variant="danger"
                  size="md"
                  leftIcon={FaTrash({ 'aria-hidden': 'true' })}
                  onClick={removeFoodDatabaseEntryFromModal}
                >
                  Delete saved food
                </Button>
              ) : <span />}
              <div className={styles.modalPrimaryActions}>
                <Button variant="secondary" size="md" onClick={() => setFoodDatabaseModal(null)}>
                  Cancel
                </Button>
                <Button variant="primary" size="md" type="submit">
                  {foodDatabaseModal.mode === 'add' ? 'Add saved food' : 'Save saved food'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default NutritionPage;
